/**
 * Turn whatever text arrived into the name of a *food*.
 *
 * The pantry's identity has always been free text written by whichever surface
 * happened to add the row, and `addItemsToPantry` stored recipe ingredient
 * *lines* verbatim. A live export of 233 rows showed what that produces:
 *
 *   "1 1/2 lb (675 g) de filets de morue (ou de poisson blanc au goût), coupés en morceaux"
 *   "fresh lime juice, plus more if desired"
 *   "dry (fino) sherry, or use white wine or dry vermouth"
 *   "pour 4 personnes"                     <- a yield line, not a food
 *   "thé (5 ml) de paprika"                <- an upstream parse left the unit behind
 *
 * No downstream matcher can recover from those, which is why six separate rows
 * of lime juice coexisted. This module is the choke point that stops them being
 * written in the first place.
 *
 * Two things it deliberately does NOT do:
 *   - cap the name at N words. Head nouns sit at opposite ends in the two
 *     languages this app handles ("filets de morue" vs "large raw shrimp"), so
 *     a positional cap is right in one language and wrong in the other. It
 *     strips descriptors from both ends instead and keeps whatever is left.
 *   - guess. When a line cannot be reduced to a food it is reported unusable,
 *     for the caller to quarantine — never silently turned into a plausible row.
 */
import { parseIngredientText } from './ingredientParser';
import { isStaple } from '../constants/staples';
import { isYieldLine } from './yieldLine';

export type UnusableReason = 'empty' | 'yield-line' | 'non-food' | 'fragment';

export interface CleanedPantryName {
  /** Cleaned display name. Empty string when `usable` is false. */
  name: string;
  /** False when the line is not a food and should not become a pantry row. */
  usable: boolean;
  /**
   * True when the food is a staple the app already assumes is on hand
   * (see `src/constants/staples.ts`). Staples never reach the grocery list and
   * always count as "have", so a row tracking one is pure noise.
   */
  isStaple: boolean;
  reason?: UnusableReason;
}

/** Non-breaking spaces and curly apostrophes both appear in the live data. */
function tidy(text: string): string {
  return text
    .replace(/[   ]/g, ' ')
    .replace(/[’ʼ]/g, "'")
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * A unit left stranded at the head by an upstream parse that ate "1 c. à " but
 * not the rest ("thé (5 ml) de paprika"). Without this the food reads as "tea".
 *
 * The alternatives are anchored with a lookahead rather than `\b`: `\b` treats
 * an accented letter as a word boundary, so a bare `c` alternative would match
 * the "C" of "Céréales" and eat it.
 */
const ORPHAN_UNIT_HEAD =
  /^(?:c\.\s*)?(?:cuill[eè]res?\s+)?(?:à\s+)?(?:th[ée]|soupe|table|tasses?)(?=[\s(.]|$)[\s.]*/i;

/** "1 boîte (400 ml) de lait de coco" — the packaging is not the food. */
const CONTAINER_HEAD =
  /^(?:conserves?|bo[iî]tes?|paquets?|sachets?|pots?|contenants?|bouteilles?|cartons?|cannes?|sacs?|blocs?|tranches?)\s+(?:de\s+|d')?(?:\d[\d.,/]*\s*(?:ml|l|g|kg|oz|lb|lbs)?\s*)?(?:chacune?\s+)?(?:de\s+|d')?/i;

/** Leading French articles/prepositions left behind once the quantity goes. */
const LEADING_CONNECTOR =
  /^(?:de\s+la\s+|de\s+l'|d'|de\s+|du\s+|des\s+|à\s+|au\s+|aux\s+|le\s+|la\s+|les\s+|l'|of\s+|the\s+)/i;

/**
 * A measure phrase standing in for a quantity: "Large pinch of red-pepper
 * flakes", "une pincée de sel", "a splash of vinegar". The parser only strips
 * NUMERIC quantities, so these survived and the grocery list showed
 * "Large pinch of" once the row was clipped to two lines (#626).
 *
 * An optional size adjective is part of the phrase, not part of the food.
 *
 * Deliberately excludes the ambiguous ones. "filet" and "noix" are measure
 * words in "un filet d'huile" and "une noix de beurre", but they are also
 * foods — "filet de porc" is pork tenderloin and "noix" is walnuts. Including
 * them cost two correct names in the eval for one uncommon phrasing, so they
 * stay out; add a table entry rather than a rule if they ever matter.
 */
const MEASURE_PHRASE_HEAD =
  /^(?:(?:a|an|une?|le|la)\s+)?(?:large|small|big|gros(?:se)?|petite?|generous|bonne?)?\s*(?:pinch(?:es)?|pinc[ée]es?|splash|drizzle|handful|poign[ée]es?|dash|soup[çc]on|knob)\s+(?:of\s+|de\s+|d'|du\s+|des\s+)/i;

/** French quantity range the shared parser does not know ("1 à 2 c. à soupe"). */
const FRENCH_RANGE_HEAD = /^\d+(?:[.,/]\d+)?\s+à\s+(?=\d)/;

/**
 * Clause openers that begin an aside rather than the food: purposes,
 * continuations and editorial notes. Everything from here on is dropped.
 */
const CLAUSE_CUTS: RegExp[] = [
  /\s+pour\s+(?:le\s+|la\s+|les\s+|l')?(?:service|servir|friture|garnir|d[ée]corer|accompagner)/i,
  /\s+for\s+(?:serving|garnish|frying|shallow-frying|drizzling|topping)/i,
  /\s+telle?s?\s+que\s+/i,
  /\s+such\s+as\s+/i,
  /\s+au\s+go[uû]t\b/i,
  /\s+to\s+taste\b/i,
  /\s+si\s+d[ée]sir[ée]\b/i,
  /\s+if\s+desired\b/i,
  /\s+divis[ée]e?s?\b/i,
  /\s+divided\b/i,
  /\s+voir\s+/i,
  /\s+see\s+/i,
  /\s*\bplus\b\s+/i,
  /\s+\+\s+/,
  /\s+r[ée]server\b/i,
  /\s+sans\s+les\s+/i,
  /\s+from\s+about\s+/i,
  /\s+d'environ\b/i,
  /\s+d'[ée]paisseur\b/i,
  /\s+about\s+[\d¼-¾]/i,
  /\s+with\s+tender\b/i,
];

/**
 * Words that describe how a food was prepared, sized or sourced. Stripped from
 * BOTH ends, which is what makes the module bilingual: French puts them after
 * the noun ("oignon blanc, émincé finement"), English before it ("peeled and
 * deveined large raw shrimp").
 *
 * Colours are absent on purpose — they are identity as often as description
 * ("red cabbage", "vin rouge", "black beans"), and `normalizeIngredientName`
 * already defers that same decision for the same reason.
 */
const EDGE_DESCRIPTORS = new Set([
  // English — state and preparation
  'fresh', 'freshly', 'raw', 'cooked', 'uncooked', 'peeled', 'deveined',
  'seeded', 'deseeded', 'pitted', 'trimmed', 'cleaned', 'rinsed', 'drained',
  'chopped', 'diced', 'minced', 'sliced', 'shredded', 'grated', 'crushed',
  'halved', 'quartered', 'cubed', 'julienned', 'softened', 'melted', 'packed',
  'boneless', 'skinless', 'toasted', 'roasted', 'squeezed', 'stemmed',
  // English — size, degree and sourcing
  'large', 'medium', 'small', 'baby', 'mini', 'extra', 'thin', 'thick',
  'thinly', 'thickly', 'finely', 'coarsely', 'roughly', 'lightly', 'loosely',
  'firmly', 'lean', 'organic', 'homemade', 'store-bought', 'whole',
  // English — counting words that stand in for the food
  'clove', 'cloves', 'bunch', 'bunches', 'head', 'heads', 'sprig', 'sprigs',
  'stalk', 'stalks', 'piece', 'pieces', 'and', 'of',
  // French — state and preparation
  'frais', 'fraiche', 'fraîche', 'fraiches', 'fraîches',
  'haché', 'hachée', 'hachés', 'hachées',
  'émincé', 'émincée', 'émincés', 'émincées',
  'ciselé', 'ciselée', 'ciselés', 'ciselées',
  'râpé', 'râpée', 'râpés', 'râpées',
  'coupé', 'coupée', 'coupés', 'coupées',
  'tranché', 'tranchée', 'tranchés', 'tranchées',
  'écrasé', 'écrasée', 'écrasés', 'écrasées',
  'épépiné', 'épépinée', 'épépinés', 'épépinées',
  'grillé', 'grillée', 'grillés', 'grillées',
  'rôti', 'rôtie', 'rôtis', 'rôties',
  'lavé', 'lavée', 'lavés', 'lavées',
  'ramolli', 'ramollie', 'fondu', 'fondue',
  // French — size, degree and counting words
  'petit', 'petite', 'petits', 'petites', 'gros', 'grosse', 'grosses',
  'grand', 'grande', 'grands', 'grandes', 'moyen', 'moyenne',
  'finement', 'grossièrement', 'légèrement', 'bien', 'chacun', 'chacune',
  'gousse', 'gousses', 'brin', 'brins', 'botte', 'bottes',
  'et', 'de', 'du', 'des', 'à', 'au', 'aux',
]);


const NON_FOOD_TERMS = [
  'bâton', 'baton', 'brochette', 'cure-dent', 'papier parchemin',
  "papier d'aluminium", 'pellicule plastique', 'sac de congélation',
  'accompagnement', 'ficelle', 'skewer', 'toothpick', 'parchment paper',
  'aluminum foil', 'plastic wrap', 'paper towel',
];

/** Word-boundary test tolerant of accents and a plural 's'. */
function hasTerm(text: string, term: string): boolean {
  const escaped = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return new RegExp(`(^|[^a-zà-ÿ])${escaped}s?([^a-zà-ÿ]|$)`, 'i').test(text);
}

const isNumericToken = (token: string) => /^[\d¼-¾⅐-⅞.,/-]+$/.test(token);

/** A unit symbol left behind by the number it belonged to ("crème à cuisson 15 %"). */
const isStrandedSymbol = (token: string) => token === '%';

/**
 * A measurement unit that has outlived its number (#601).
 *
 * Once the quantity is stripped the unit is left holding the head or the tail
 * of the name, and nothing downstream recognises it as anything but a word:
 * "40 cl de lait de coco" was stored, and shown in the Garde-manger, as "cl de
 * lait de coco". Dropping it at the edges is safe because it is only ever
 * reached after `stripEdgeDescriptors` has already peeled the number off, and
 * because the caller never strips the last surviving token — a row whose whole
 * name is "g" keeps it and gets quarantined as a fragment instead.
 *
 * Abbreviations only. Written-out units ("tasse", "cuillère") are the head of
 * ORPHAN_UNIT_HEAD's job, and "livre"/"once" are also foods.
 */
const UNIT_TOKENS = new Set([
  'ml', 'cl', 'dl', 'l', 'g', 'kg', 'mg', 'oz', 'lb', 'lbs',
  'tsp', 'tbsp', 'tbs', 'cup', 'cups', 'pt', 'qt',
]);
const isStrandedUnit = (token: string) => UNIT_TOKENS.has(token);

function stripEdgeDescriptors(text: string): string {
  let tokens = text.split(' ').filter(Boolean);
  const droppable = (token: string) => {
    const bare = token.toLowerCase().replace(/[.,;:]/g, '');
    return (
      EDGE_DESCRIPTORS.has(bare) ||
      isNumericToken(bare) ||
      isStrandedSymbol(bare) ||
      isStrandedUnit(bare)
    );
  };

  // Never strip the last surviving token — "whole chicken" must not become "".
  while (tokens.length > 1 && droppable(tokens[0])) tokens = tokens.slice(1);
  while (tokens.length > 1 && droppable(tokens[tokens.length - 1])) tokens = tokens.slice(0, -1);

  return tokens.join(' ');
}

/**
 * A word that only qualifies a food and can never BE one. Returning one of
 * these as the name is always a defect, never a near-miss: "red" is not a
 * shopping-list item.
 */
const BARE_MODIFIERS = new Set([
  'red', 'green', 'white', 'black', 'yellow', 'brown', 'blue', 'golden',
  'dark', 'light', 'sweet', 'hot', 'mild', 'spicy', 'fresh', 'dried',
  'large', 'medium', 'small', 'whole', 'ground', 'raw', 'plain',
  'rouge', 'vert', 'verte', 'blanc', 'blanche', 'noir', 'noire', 'jaune',
  'brun', 'brune', 'doré', 'dorée', 'foncé', 'foncée', 'clair', 'claire',
  'doux', 'douce', 'fort', 'forte', 'piquant', 'piquante', 'frais', 'fraîche',
  'séché', 'séchée', 'gros', 'grosse', 'petit', 'petite', 'entier', 'entière',
  'moulu', 'moulue', 'nature',
]);

/** True when the whole string is a single qualifier and names no food at all. */
function isBareModifier(text: string): boolean {
  const tokens = text.trim().split(/\s+/).filter(Boolean);
  if (tokens.length !== 1) return false;
  return BARE_MODIFIERS.has(tokens[0].toLowerCase().replace(/[.,;:]/g, ''));
}

/**
 * True when nothing in the string names a food — every token only describes
 * one. Broader than `isBareModifier`, which asks about a single colour-like
 * word; this also catches the preparation vocabulary ("boneless", "thin",
 * "skinless") that `stripEdgeDescriptors` leaves standing because it never
 * strips the last surviving token.
 */
function namesNothing(text: string): boolean {
  const tokens = text.trim().split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return true;
  return tokens.every(token => {
    const bare = token.toLowerCase().replace(/[.,;:]/g, '');
    return (
      EDGE_DESCRIPTORS.has(bare) ||
      BARE_MODIFIERS.has(bare) ||
      isNumericToken(bare) ||
      isStrandedUnit(bare) ||
      isStrandedSymbol(bare)
    );
  });
}

/** The noun an alternation shares — the last word of the final alternative. */
function sharedHeadNoun(text: string): string {
  const tokens = text.trim().split(/\s+/).filter(Boolean);
  return tokens.length ? tokens[tokens.length - 1] : '';
}

/**
 * "sweet or hot paprika" is paprika; cutting at "or" would keep "sweet".
 * In both languages the alternatives share the head noun that follows them, so
 * the last alternative is the one carrying the food.
 */
function lastAlternative(text: string): string {
  const matches = [...text.matchAll(/\s+(?:or|ou)\s+/gi)];
  if (matches.length === 0) return text;
  const last = matches[matches.length - 1];
  const head = text.slice(0, last.index!).trim();
  const tail = text.slice(last.index! + last[0].length).trim();
  if (!tail) return head || text;

  // "or use white wine" — an imperative restarts the phrase rather than
  // completing it, so the left-hand side was the real food.
  if (/^(?:use|utilise[rz]?)\b/i.test(tail)) return head || text;

  // Only a short left side is a bare modifier awaiting the shared noun
  // ("sweet or hot paprika"). A long one already names the food, and the
  // alternatives are trailing qualifiers instead ("gnocchis frais ou sous
  // vide") — taking the tail there would return "sous vide".
  const headTokens = head.split(' ').filter(Boolean).length;
  const tailTokens = tail.split(' ').filter(Boolean).length;

  if (headTokens <= 2 && tailTokens <= 3) {
    // Guard: the tail must still name something. "Sherry or red" would hand
    // back "red", which is a colour, not a food.
    return isBareModifier(tail) ? head || text : tail;
  }

  // The head is long — but if it is ITSELF a chain of alternatives, returning it
  // whole just defers the problem: the next reduce pass splits it again and can
  // bottom out on a bare modifier. "Sherry or red or white wine vinegar" walked
  // exactly that path to "red" (#626). A chain shares one head noun, so keep the
  // FIRST alternative and give it that noun back.
  if (/\s+(?:or|ou)\s+/i.test(head)) {
    const first = head.split(/\s+(?:or|ou)\s+/i)[0].trim();
    const noun = sharedHeadNoun(tail);
    if (first && noun && !isBareModifier(first)) {
      return first.toLowerCase().endsWith(noun.toLowerCase()) ? first : `${first} ${noun}`;
    }
  }

  return head || text;
}

/**
 * Everything after the comma has been decided. Split out from `reducePass` so
 * the comma decision can try more than one cut and judge each by its result.
 */
function reduceFrom(input: string): string {
  let text = tidy(input);
  if (!text) return '';

  text = lastAlternative(text);

  for (const cut of CLAUSE_CUTS) {
    const m = text.match(cut);
    if (m && m.index !== undefined && m.index > 0) text = text.slice(0, m.index);
  }
  text = tidy(text);

  // Strip the leading/trailing quantity + unit. `parseIngredientText` already
  // handles leading, trailing and French formats, including unicode fractions —
  // do not reimplement it here.
  text = tidy(text.replace(FRENCH_RANGE_HEAD, ''));
  text = tidy(parseIngredientText(text).remainder || text);

  // Repair the head: orphan units, packaging and connectors would otherwise
  // survive as the food.
  let previous = '';
  while (text !== previous) {
    previous = text;
    text = text.replace(ORPHAN_UNIT_HEAD, '');
    text = text.replace(CONTAINER_HEAD, '');
    text = text.replace(MEASURE_PHRASE_HEAD, '');
    // En and em dashes bullet a French ingredient list as often as a hyphen
    // does; leaving one attached blocked the head quantity from being peeled
    // off, and the unit stayed behind as the food (#601).
    text = text.replace(/^[-–—‒―+•*\s.]+/, '');
    text = text.replace(LEADING_CONNECTOR, '');
    text = tidy(text);
  }

  // A quantity stranded mid-string ("Le jus de ½ lime") is not part of the name,
  // and neither is the symbol it was attached to ("crème 15 % à cuisson").
  text = tidy(
    text
      .split(' ')
      .filter(
        (token, _i, all) =>
          all.length === 1 || (!isNumericToken(token) && !isStrandedSymbol(token))
      )
      .join(' ')
  );

  return stripEdgeDescriptors(text).replace(/[\s,.;:'-]+$/, '').replace(/^[\s,.;:-]+/, '').trim();
}

/** One reduction pass. Run twice, because an alternative can reintroduce a quantity. */
function reducePass(input: string): string {
  let text = tidy(input);
  if (!text) return '';

  // Parentheticals are always commentary — metric equivalents, brand notes,
  // "(skin on or off)". Remove them before any cut so an inline aside cannot
  // truncate the food ("dry (fino) sherry" must not become "dry").
  text = tidy(text.replace(/\([^)]*\)/g, ' '));

  // A comma usually ends the food and begins commentary — "panais, pelés et
  // râpés". Two shapes break that, and both used to produce a pantry row named
  // after something that is not a food (#601):
  //
  //   - a variety list, where the comma separates alternatives and the head
  //     noun is still to come: "Thai, holy or sweet basil leaves";
  //   - an English descriptor stack, where the descriptors come BEFORE the noun
  //     and a comma sits inside them: "1 pound thin, round rice noodles" was
  //     reduced to "thin", and "boneless, skinless chicken breasts" to
  //     "boneless".
  //
  // Rather than add a rule per shape, judge a cut by what it leaves: try each
  // comma in turn and keep the first one that still names something. A result
  // made only of descriptors means the cut landed inside them, so move right.
  const commas = [...text.matchAll(/[,;]/g)]
    .map(m => m.index!)
    .filter(i => i > 0);

  const varietyList =
    commas.length > 0 &&
    text.slice(0, commas[0]).trim().split(' ').length === 1 &&
    /\s+(?:or|ou)\s+/i.test(text.slice(commas[0]));

  const candidates = varietyList ? [text] : [...commas.map(i => text.slice(0, i)), text];

  let last = '';
  for (const candidate of candidates) {
    last = reduceFrom(candidate);
    if (last && !namesNothing(last)) return last;
  }
  return last;
}

/**
 * Reduce an arbitrary line to the food it names.
 *
 * @param line - raw text from a recipe ingredient, grocery row, or manual entry
 */
export function cleanPantryName(line: string): CleanedPantryName {
  const unusable = (reason: UnusableReason): CleanedPantryName => ({
    name: '', usable: false, isStaple: false, reason,
  });

  const raw = tidy(line ?? '');
  if (!raw) return unusable('empty');
  // Recipe scaffolding rather than an ingredient. Shared with the scraper, the
  // recipe-detail rows and the grocery list so all four agree on what a yield
  // line is (#594).
  if (isYieldLine(raw)) return unusable('yield-line');

  let text = reducePass(raw);
  const second = reducePass(text);
  if (second) text = second;

  if (!text) return unusable('empty');
  if (NON_FOOD_TERMS.some(term => hasTerm(text, term))) return unusable('non-food');

  // Plain water. Exact match only, so "eau de coco" and "sparkling water" —
  // things people actually buy — are untouched.
  if (/^(?:eau|water)$/i.test(text)) return unusable('non-food');

  // Debris from a broken upstream parse ("thé (2", "d'it") is never a food.
  if (!/\p{L}{3}/u.test(text)) return unusable('fragment');

  // What survived is a measurement, not a food. "1/3 cup plus 2 tablespoons
  // extra-virgin olive oil" reduces to "cup", because the clause cut at "plus"
  // is right for "plus more if desired" and wrong for a second quantity of the
  // same ingredient. Rather than store "cup", say so — the caller quarantines
  // it, which is what this module does with every other line it cannot reduce
  // to a food (#601).
  if (text.split(' ').every(token => isStrandedUnit(token.toLowerCase()))) {
    return unusable('fragment');
  }

  // "hot paprika" is still paprika. The staple list holds bare foods, so fall
  // back to the head noun when the full phrase misses.
  const headNoun = text.split(' ').slice(-1)[0];
  const staple = isStaple(text) || (text.includes(' ') && isStaple(headNoun));

  return { name: text, usable: true, isStaple: staple };
}

/**
 * Convenience wrapper for callers that only want a name and are happy to keep
 * the original text when the line cannot be reduced. Never returns empty.
 */
export function pantryNameFromIngredientLine(line: string): string {
  const cleaned = cleanPantryName(line);
  return cleaned.usable ? cleaned.name : tidy(line ?? '');
}
